import React from 'react';
import {
	type FieldPath,
	type FieldValues,
	type PathValue,
} from 'react-hook-form';
import {
	EmailsField,
	type EmailsFieldProps,
} from '@netx/core-components/EmailsField';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {splitEventHandler} from '@netx/core-next/utils';
import {combineRefs} from '@netx/core-next/utils/react';
import {i18n} from '@netx/core/i18n';
import {
	ReactHookFormControl,
	type WithReactHookFormControlFieldProps,
} from '../ReactHookFormControl';
import {validateEmail} from '../validators';

export interface ReactHookFormEmailsFieldProps<
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
		EmailsFieldProps<DisableClearable>,
		TFieldValues,
		TName
	> {}

export const ReactHookFormEmailsFieldPrimitive = <
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		control,
		ControllerProps,
		...props
	}: ReactHookFormEmailsFieldProps<DisableClearable, TFieldValues, TName>,
	ref: any,
) => {
	const fieldName = props.name ?? 'email';
	return (
		<ReactHookFormControl
			{...ControllerProps}
			control={control}
			name={fieldName as TName}
			render={({field, formState}) => (
				<EmailsField
					error={formState.errors[fieldName]?.message?.toString()}
					{...props}
					{...field}
					disabled={props.disabled || field.disabled}
					ref={ref}
					inputRef={combineRefs(props.inputRef, field.ref)}
					value={field.value ?? ([] as PathValue<TFieldValues, TName>)}
					onChange={useConstCallback(
						splitEventHandler(
							props.onChange,
							// this is not a classic form change event
							// it comes from an option click within autocomplete
							(event, value) => field.onChange(value),
						),
					)}
				/>
			)}
			rules={{
				required: props.required,
				validate: async (value, values) => {
					const valid = (value as string[]).every(
						(val) => validateEmail(val) === true,
					);
					return valid || i18n.t('i18n.invalidEmailAddress');
				},
				...ControllerProps?.rules,
			}}
		/>
	);
};

/**
 * `react-hook-form` controlled `EmailsField` component
 */
export const ReactHookFormEmailsField = React.forwardRef(
	ReactHookFormEmailsFieldPrimitive,
) as <
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: ReactHookFormEmailsFieldProps<DisableClearable, TFieldValues, TName>,
) => JSX.Element;

export default ReactHookFormEmailsField;
